import { useSyncExternalStore } from 'react';

/**
 * Reactive "is the applied theme dark?" — reads the single source of truth
 * (`[data-bs-theme]` on <html>, owned by applyTheme in theme.ts) and re-renders
 * when it flips, whatever the cause (the picker, an OS color-scheme change, an
 * embedded viewer, another tab).
 *
 * Mirrors ts-mono's `useResolvedIsDark` (a `useSyncExternalStore` over a
 * `MutationObserver` on the same attribute). Most Hawk UI reacts to the theme
 * in pure CSS via Tailwind's `dark:` variant — this hook is only for surfaces
 * that style themselves in JS and can't see that variant (e.g. CodeMirror).
 */
const subscribe = (onChange: () => void): (() => void) => {
  if (
    typeof MutationObserver === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return () => {
      /* no DOM to observe (SSR); nothing to unsubscribe */
    };
  }
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-bs-theme'],
  });
  return () => observer.disconnect();
};

const getSnapshot = (): boolean =>
  document.documentElement.getAttribute('data-bs-theme') === 'dark';

export const useIsDarkTheme = (): boolean =>
  // No SSR today; light is the safe server default if a render ever runs there.
  useSyncExternalStore(subscribe, getSnapshot, () => false);
